import { RyeconClock, RyeconTable, RyeconTableRow } from '@dbt-labs/sourdough';

import DetailTabs, { TabInfo } from '@/components/DetailTabs';
import { useSeed } from '@/hooks/dbtStrategy/useSeed';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useValidatedParams } from '@/hooks/params';
import { useSetMissingCatalog } from '@/hooks/useSetMissingCatalog';
import { useSetMissingTests } from '@/hooks/useSetMissingTests';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { getDatabaseObjectType } from '@/util/database';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import { useWarehouseHeaderIcon } from '../../hooks/useWarehouseHeaderIcon';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { ColumnsView } from '../Tabs/ColumnsView';
import { GeneralView } from '../Tabs/GeneralView';

const tabs: TabInfo[] = [{ type: 'general' }, { type: 'columns' }];

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const SeedView = ({ uniqueId }: ResourceParams) => {
  const { environmentType } = useValidatedParams();
  const seedQuery = useSeed(uniqueId);
  const seed = seedQuery.fetching ? null : seedQuery.data;
  const appliedData = seed?.applied;
  useSetMissingCatalog(appliedData);
  useSetMissingTests(appliedData);

  const warehouseHeaderIcon = useWarehouseHeaderIcon({
    uniqueId,
    environmentType,
  });

  if (seedQuery.error) return <>{seedQuery.error.stack}</>;
  const date = seed?.applied?.executionInfo?.executeCompletedAt
    ? new Date(seed?.applied.executionInfo.executeCompletedAt)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;
  const objectType =
    seed?.applied?.catalog?.type && getDatabaseObjectType(seed?.applied?.catalog.type);
  const rowCount = seed?.applied?.catalog?.rowCountStat ?? undefined;
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('seed')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Last run ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }
  if (objectType) {
    headerIcons.push({
      ryecon: RyeconTable,
      text: `${objectType}`,
    });
  }
  if (warehouseHeaderIcon) {
    headerIcons.push(warehouseHeaderIcon);
  }
  if (rowCount) {
    headerIcons.push({
      ryecon: RyeconTableRow,
      text: `${rowCount.toLocaleString()} rows`,
    });
  }

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={uniqueId.remainder}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[seed?.applied?.patchPath, seed?.applied?.filePath]}
        name={seed?.applied?.name}
        resourceType="seed"
        updatedDate={date}
      />
      <DetailTabs tabs={tabs} show={!!seed}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView
                  resource={seed?.applied as GenericResourceNode}
                  uniqueId={uniqueId}
                  description={seed?.applied?.description}
                  meta={seed?.applied?.meta}
                />
              )}
              {activeTab === 'columns' && seed?.applied && (
                <ColumnsView resource={seed.applied} />
              )}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
